import { compare, divide, subtract, toUnits } from './decimal';

/** Ordered / received / billed per purchase-order line, expressed in the line's own unit. */
export interface LineProgress {
  ordered: string;
  received: string;
  billed: string;
  outstanding: string;
  /** 0–100 */
  receivedPct: number;
  billedPct: number;
}

interface ProgressLine {
  qty: string;
  qtyBase: string;
  factor: string;
  qtyReceived: string;
  qtyBilled: string;
  qtyToReceive: string;
}

const SCALE = 10;

function pct(part: string, whole: string): number {
  const w = toUnits(whole, SCALE);
  if (w <= 0n) return 0;
  const p = toUnits(part, SCALE);
  const capped = p > w ? w : p < 0n ? 0n : p;
  // one decimal of precision, as a number for the progress visuals
  return Number((capped * 1000n) / w) / 10;
}

/** API base quantities → the line unit (`qtyReceived` 160 pcs at 4 pcs/box → 40 boxes). */
export function inLineUnit(qtyBase: string, factor: string): string {
  return trim(divide(qtyBase, factor, 4));
}

export function trim(value: string): string {
  if (!value.includes('.')) return value;
  return value.replace(/0+$/, '').replace(/\.$/, '');
}

export function lineProgress(line: ProgressLine): LineProgress {
  return {
    ordered: trim(line.qty),
    received: inLineUnit(line.qtyReceived, line.factor),
    billed: inLineUnit(line.qtyBilled, line.factor),
    outstanding: trim(line.qtyToReceive),
    receivedPct: pct(line.qtyReceived, line.qtyBase),
    billedPct: pct(line.qtyBilled, line.qtyBase),
  };
}

/**
 * Whole-order progress by base quantity (lines in different units are comparable in base units only).
 * Received counts accepted goods; rejected goods stay outstanding.
 */
export function orderProgress(lines: ProgressLine[]): {
  receivedPct: number;
  billedPct: number;
  outstandingLines: number;
} {
  let ordered = 0n;
  let received = 0n;
  let billed = 0n;
  for (const l of lines) {
    const o = toUnits(l.qtyBase, SCALE);
    const r = toUnits(l.qtyReceived, SCALE);
    const b = toUnits(l.qtyBilled, SCALE);
    ordered += o;
    received += r > o ? o : r;
    billed += b > o ? o : b;
  }
  const share = (x: bigint) => (ordered === 0n ? 0 : Number((x * 1000n) / ordered) / 10);
  return {
    receivedPct: share(received),
    billedPct: share(billed),
    outstandingLines: lines.filter((l) => compare(l.qtyToReceive, '0') > 0).length,
  };
}

/** Received but not yet billed, in base units, for the order (drives the "Bill" action). */
export function hasUnbilled(lines: ProgressLine[]): boolean {
  return lines.some((l) => compare(subtract(l.qtyReceived, l.qtyBilled), '0') > 0);
}

/** Share of a bill that is paid (0–100). */
export function paidPct(total: string, paid: string): number {
  return pct(paid, total);
}
